// Pure helpers for the comics category (design_documents/Comics/).
//
// A comic is either a single issue (Metron) or a collected edition — TPB / HC /
// omnibus — from Metron or Open Library. Both live in one store, keyed by
// `comic_key` = "<source>:<external_id>", because a comic is the only media type
// with two sources.

export const COMIC_SOURCES = ["metron", "openlibrary"];

// What the item IS (catalog-ish, user-correctable) — not how the user owns it
// (that's the shared ownership `formats`).
export const COMIC_FORMATS = ["issue", "tpb", "hc", "omnibus", "other"];
const COMIC_FORMAT_LABELS = {
	issue: "Single issue",
	tpb: "Trade paperback",
	hc: "Hardcover",
	omnibus: "Omnibus",
	other: "Other",
};
const COMIC_FORMAT_SHORT = {
	issue: "Issue",
	tpb: "TPB",
	hc: "HC",
	omnibus: "Omnibus",
	other: "Other",
};

export function comicFormatLabel(format) {
	return COMIC_FORMAT_LABELS[format] ?? "Comic";
}

export function comicFormatShort(format) {
	return COMIC_FORMAT_SHORT[format] ?? "Comic";
}

// Collected editions get the "Collects…" editor; single issues don't.
export function isCollection(comic) {
	return comic?.format === "tpb" || comic?.format === "hc" || comic?.format === "omnibus";
}

export function comicKey(source, externalId) {
	if (!COMIC_SOURCES.includes(source)) throw new Error(`Unknown comic source ${source}`);
	if (externalId == null || externalId === "") throw new Error("A comic needs an external id");
	return `${source}:${externalId}`;
}

export function splitComicKey(key) {
	const i = String(key ?? "").indexOf(":");
	if (i < 0) return null;
	return { source: key.slice(0, i), external_id: key.slice(i + 1) };
}

// "X-Men (2004)" — the run label Metron uses everywhere.
export function seriesLabel(series) {
	if (!series?.name) return null;
	return series.year_began ? `${series.name} (${series.year_began})` : series.name;
}

// The display title for a Metron issue detail (worker /comic/:id shape). A
// single issue reads "X-Men (2004) #188"; a collected edition reads like a book
// ("Marvel Masterworks: The Uncanny X-Men Vol. 2"), with its own title appended
// when Metron has one.
export function metronComicTitle(detail) {
	const series = detail?.series;
	if (!series?.name) return detail?.title ?? "Untitled comic";
	const format = detail.format ?? series.format ?? "issue";
	if (format === "tpb" || format === "hc" || format === "omnibus") {
		let title = series.name;
		if (detail.number && detail.number !== "1") title += ` Vol. ${detail.number}`;
		if (detail.title) title += `: ${detail.title}`;
		return title;
	}
	const base = `${seriesLabel(series)} #${detail.number ?? "?"}`;
	return detail.title ? `${base}: ${detail.title}` : base;
}

// ---------- issue ranges ----------

// Numbers are strings in Metron ("188", "34.1", "-1", "1a"); sort on the leading
// number, then string compare (so "1a" follows "1").
export function issueNumberValue(n) {
	const m = String(n ?? "").match(/^-?\d+(\.\d+)?/);
	return m ? parseFloat(m[0]) : Number.NaN;
}

export function compareIssueNumbers(a, b) {
	const va = issueNumberValue(a);
	const vb = issueNumberValue(b);
	if (Number.isNaN(va) && Number.isNaN(vb)) return String(a).localeCompare(String(b));
	if (Number.isNaN(va)) return 1;
	if (Number.isNaN(vb)) return -1;
	return va - vb || String(a).localeCompare(String(b));
}

// Parse a user-typed range list ("188-199, 201, 205–207") into tokens
// [{ from, to }] with whole-number bounds, or { error } on anything we can't
// read. Hyphen, en dash and "to" all work; spaces and "#" are ignored.
export function parseRanges(text) {
	const raw = String(text ?? "").replace(/#/g, "").trim();
	if (!raw) return { error: "Enter the issue numbers, e.g. 188–199." };
	const parts = raw.split(/[,;]+/).map((p) => p.trim()).filter(Boolean);
	const out = [];
	for (const part of parts) {
		const m = part.match(/^(-?\d+)\s*(?:[-–—]|to)\s*(-?\d+)$/i) ?? part.match(/^(-?\d+)$/);
		if (!m) return { error: `Can't read "${part}". Use numbers and ranges like 188–199.` };
		const from = Number(m[1]);
		const to = m[2] != null ? Number(m[2]) : from;
		if (to < from) return { error: `"${part}" runs backwards.` };
		if (to - from > 2000) return { error: `"${part}" is too long a range.` };
		out.push({ from, to });
	}
	return { ranges: mergeRanges(out) };
}

function mergeRanges(list) {
	const sorted = list.slice().sort((a, b) => a.from - b.from);
	const out = [];
	for (const r of sorted) {
		const last = out[out.length - 1];
		if (last && r.from <= last.to + 1) last.to = Math.max(last.to, r.to);
		else out.push({ ...r });
	}
	return out;
}

// [{from,to}] → "188–199, 201"
export function formatRanges(ranges) {
	return (ranges ?? []).map((r) => (r.from === r.to ? `${r.from}` : `${r.from}–${r.to}`)).join(", ");
}

// "188-199,201" → "188–199, 201" (canonical text), or null if unreadable.
export function normalizeRanges(text) {
	const parsed = parseRanges(text);
	return parsed.error ? null : formatRanges(parsed.ranges);
}

// Does a canonical ranges string include issue number `n`? Non-integer numbers
// ("34.1", "1a") count when their whole part falls inside a range.
export function rangesInclude(rangesText, n) {
	const parsed = parseRanges(rangesText);
	if (parsed.error) return false;
	const v = Math.trunc(issueNumberValue(n));
	if (Number.isNaN(v)) return false;
	return parsed.ranges.some((r) => v >= r.from && v <= r.to);
}

// Whole issue numbers → a "Collects…" ranges string ("188–193, 197"); the
// non-integer ones ("34.1", "1a") are dropped — a range can't express them.
export function rangesFromNumbers(numbers) {
	const ints = (numbers ?? [])
		.map((n) => String(n))
		.filter((n) => /^-?\d+$/.test(n))
		.map(Number);
	if (ints.length === 0) return null;
	const sorted = [...new Set(ints)].sort((a, b) => a - b);
	const ranges = [];
	for (const v of sorted) {
		const last = ranges[ranges.length - 1];
		if (last && v === last.to + 1) last.to = v;
		else ranges.push({ from: v, to: v });
	}
	return formatRanges(ranges);
}

// Collapse a list of issue numbers into display ranges: ["188","189","190","197"]
// → "#188–190, #197". Non-integer numbers stand alone.
export function collapseIssueNumbers(numbers) {
	const sorted = [...new Set((numbers ?? []).map(String))].sort(compareIssueNumbers);
	const out = [];
	let start = null;
	let prev = null;
	const flush = () => {
		if (start === null) return;
		out.push(start === prev ? `#${start}` : `#${start}–${prev}`);
		start = prev = null;
	};
	for (const n of sorted) {
		const v = issueNumberValue(n);
		if (!Number.isInteger(v) || String(v) !== n) {
			flush();
			out.push(`#${n}`);
			continue;
		}
		if (start !== null && v === prev + 1) {
			prev = v;
			continue;
		}
		flush();
		start = prev = v;
	}
	flush();
	return out.join(", ");
}

// ---------- credits ----------

// Metron role names grouped the way the creator view filters them (spike §roles).
export const ROLE_GROUPS = {
	writer: ["Writer", "Script", "Story", "Plot"],
	art: ["Artist", "Penciller", "Breakdowns", "Illustrator", "Layouts", "Inker", "Finishes", "Embellisher"],
	cover: ["Cover"],
};

export function roleGroupOf(role) {
	for (const [group, roles] of Object.entries(ROLE_GROUPS)) {
		if (roles.includes(role)) return group;
	}
	return null;
}

// Credits for the detail panel: Writing / Art / Cover lines, each a list of
// names (with their specific role when it isn't the obvious one). Editors,
// letterers, colourists etc. are left out — they'd drown the panel.
export function creditLines(creators) {
	const lines = { writer: [], art: [], cover: [] };
	for (const c of creators ?? []) {
		const roles = c.roles ?? [];
		if (roles.length === 0) {
			// Open Library authors carry no roles — show them as writers.
			lines.writer.push(c.name);
			continue;
		}
		for (const group of Object.keys(lines)) {
			const mine = roles.filter((r) => ROLE_GROUPS[group].includes(r));
			if (mine.length === 0) continue;
			const generic = group === "writer" ? ["Writer"] : group === "art" ? ["Artist"] : ["Cover"];
			const specific = mine.filter((r) => !generic.includes(r));
			lines[group].push(specific.length ? `${c.name} (${specific.join(", ").toLowerCase()})` : c.name);
		}
	}
	return lines;
}

// ---------- source records → addComic() fields ----------

// addComic() fields from a Metron issue detail (worker /comic/:id).
export function comicFromMetron(row, full) {
	return {
		source: "metron",
		external_id: String(full.issue_id ?? row.external_id),
		title: metronComicTitle(full),
		cover_url: full.cover_url ?? row.cover_url ?? null,
		format: full.format ?? "issue",
		series: full.series
			? { id: full.series.id, name: full.series.name, year_began: full.series.year_began ?? null }
			: null,
		number: full.number ?? null,
		publisher: full.publisher ?? null,
		cover_date: full.cover_date ?? null,
		release_year: full.cover_date ? Number(full.cover_date.slice(0, 4)) : null,
		isbn: full.isbn ?? null,
		page_count: full.page_count ?? null,
		summary: full.summary ?? null,
		story_titles: full.story_titles ?? [],
		creators: (full.credits ?? []).map((c) => ({ id: c.creator_id, name: c.name, roles: c.roles ?? [] })),
		reprints: (full.reprints ?? []).map((r) => ({ id: r.issue_id, label: r.label })),
		gcd_id: full.gcd_id ?? null,
	};
}

// addComic() fields from an Open Library work (search row + /book/:id). Open
// Library can't tell a hardcover from a paperback — default to TPB; the user can
// change it on the detail screen.
export function comicFromOpenLibrary(row, full) {
	const b = row.raw ?? {};
	return {
		source: "openlibrary",
		external_id: b.ol_id ?? row.external_id,
		title: full?.title ?? b.title ?? "Untitled",
		cover_url: full?.cover_url ?? b.cover_url ?? null,
		format: "tpb",
		release_year: b.published_year ?? null,
		summary: full?.summary ?? null,
		creators: (b.authors ?? []).map((name) => ({ id: null, name, roles: [] })),
	};
}
